import { Injectable } from '@angular/core';
import { CommunicationService } from './communication.service';
import { UrlSegments } from '../jurisdiction/jurisdiction.constants';

@Injectable()
export class ZerezService {
  constructor(private communicationService: CommunicationService) {}

  getZerezData = () => this.communicationService.getData<ZerezConfigData>(UrlSegments.ZerezApiConfiguration);

  updateZerezData = (config: ZerezConfigData) =>
    this.communicationService.updateData<ZerezConfigData>(UrlSegments.ZerezApiConfiguration, config);

  getUnitByZerezId = (zerezId: string) => this.communicationService.getData<ZerezApiResponse>(`${UrlSegments.ZerezApiUnit}/${zerezId}`);
}

export interface ZerezConfigData {
  client_id: string;
  client_secret: string;
  last_changed?: string;
}

// Enums
export enum NetFeedIn {
  SinglePhase = 'Single Phase (DE: SinglePhase)',
  TwoPhase = 'Two-Phase (DE: TwoPhase)',
  ThreePhase = 'Three-Phase (DE: ThreePhase)',
  Unknown = 'Unknown (DE: Unknown)',
}

export enum CertificationValidationStatus {
  Verified = 'Verified (DE: Verifiziert)',
  VerificationPending = 'Verification Pending (DE: Verifizierung ausstehend)',
  OngoingFeedbackProcess = 'Ongoing Feedback Process (DE: Feedbackverfahren läuft)',
}

export enum Category {
  Inverter = 'Inverter (DE: Umrichter)',
  StorageInverter = 'Storage Inverter (DE: Umrichter des Speichers)',
  AsynchronousGenerator = 'Asynchronous Generator (DE: Asynchrongenerator)',
  SynchronousGenerator = 'Synchronous Generator (DE: Synchrongenerator)',
  FuelCell = 'Fuel Cell (DE: Brennstoffzelle)',
  UndervoltageProtection = 'Undervoltage protection (DE: NA-Schutz)',
  PAVEMonitoring = 'PAV (Protection, Automation, Control), E-Monitoring (DE: PAV,E-Überwachung)',
  VoltageRegulator = 'Voltage Regulator (DE: Spannungsregler)',
  ProtectiveDeviceIntermediate = 'Protective Device (Intermediate) (DE: Schutzgerät (Zwischengelagert))',
  EZAController = 'EZA Controller (DE: EZA-Regler)',
  Other = 'Other (DE: Andere)',
}

export enum PrimaryEnergySource {
  Wind = 'Wind (DE: Wind)',
  Photovoltaic = 'Photovoltaic (DE: Photovoltaic)',
  PVStorage = 'PV / Storage (DE: PV / Speicher)',
  OtherFuels = 'Other Fuels (DE: Gasförmige und flüssige Brennstoffe)',
  Storage = 'Storage (DE: Speicher)',
  Water = 'Water (DE: Wasser)',
  Biogas = 'Biogas (DE: Biogas)',
  Other = 'Other (DE: Sonstiges)',
  NotApplicable = 'N/A (DE: N/A)',
}

// Interfaces
export interface CertificateNorm {
  name?: string;
}

export interface CertificateFile {
  certificate_file_name?: string;
  certificate_file_type?: string;
  certificate_file_description?: string;
  certificate_file_storage_location?: string;
  expiration?: Date;
}

export interface Certificate {
  deep_link?: string;
  certificate_number?: string;
  zerez_id?: string;
  certificate_type?: string;
  replaced_by?: string;
  certificate_holder?: string;
  certificate_authority?: string;
  certificate_norms?: CertificateNorm[];
  certificate_validity_status?: string;
  certificate_issue_date?: Date;
  validity_start_date?: Date;
  restrictions?: string;
  software_environment_version_number?: string;
  norms?: string[];
  validity_end_date?: Date;
  validation_status?: CertificationValidationStatus;
  category?: Category;
  primary_energy_source?: PrimaryEnergySource;
  certificate_files?: CertificateFile[];
}

export interface ZerezApiResponse {
  deep_link?: string;
  unit_zerez_code?: string;
  model_number?: string;
  model_name?: string;
  replaced_by_zerez_code?: string;
  maximum_active_power_kw?: number;
  maximum_apparent_power_kva?: number;
  rated_active_power_kw?: number;
  rated_apparent_power_kva?: number;
  maximum_active_power_at_0_9_pf_kw?: number;
  rated_voltage_v?: number;
  rated_current_a?: number;
  peak_short_circuit_current_a?: number;
  initial_short_circuit_alternating_current_a?: number;
  worst_case_operation_scenario_value?: number;
  net_feed_in_details?: NetFeedIn;
  flicker_system_coefficient_at_30_cu?: number;
  flicker_system_coefficient_at_50_cu?: number;
  flicker_system_coefficient_at_70_cu?: number;
  flicker_system_coefficient_at_85_cu?: number;

  // Operation 1 properties
  max_switch_operations_operation1_n10?: number;
  max_number_of_operations_operation1_n120?: number;
  voltage_variation_factor_at_operation1_30_kwv?: number;
  voltage_variation_factor_at_operation1_50_kwv?: number;
  voltage_variation_factor_at_operation1_70_kwv?: number;
  voltage_variation_factor_at_operation1_85_kwv?: number;
  flicker_step_factor_at_operation1_30_kfv?: number;
  flicker_step_factor_at_operation1_50_kfv?: number;
  flicker_step_factor_at_operation1_70_kfv?: number;
  flicker_step_factor_at_operation1_85_kfv?: number;

  // Operation 2 properties
  max_switch_operations_operation2_n10?: number;
  max_number_of_operations_operation2_n120?: number;
  voltage_variation_factor_at_operation2_30_kwv?: number;
  voltage_variation_factor_at_operation2_50_kwv?: number;
  voltage_variation_factor_at_operation2_70_kwv?: number;
  voltage_variation_factor_at_operation2_85_kwv?: number;
  flicker_step_factor_at_operation2_30_kfv?: number;
  flicker_step_factor_at_operation2_50_kfv?: number;
  flicker_step_factor_at_operation2_70_kfv?: number;
  flicker_step_factor_at_operation2_85_kfv?: number;

  // Operation 3 properties
  max_no_of_switching_operations_operation3_n10?: number;
  max_no_of_switching_operations_operation3_n120?: number;
  voltage_variation_factor_operation3_30_kuf?: number;
  voltage_variation_factor_operation3_50_kuf?: number;
  voltage_variation_factor_operation3_70_kuf?: number;
  voltage_variation_factor_operation3_85_kuf?: number;
  flicker_step_factor_operation3_30_kff?: number;
  flicker_step_factor_operation3_50_kff?: number;
  flicker_step_factor_operation3_70_kff?: number;
  flicker_step_factor_operation3_85_kff?: number;

  // Fast voltage change properties
  fast_voltage_change_turn_on_without_specifications?: number;
  fast_voltage_change_worst_case_during_generator_stage_switching?: number;
  fast_voltage_change_turn_on_at_rated_conditions?: number;
  fast_voltage_change_turn_off_at_rated_power?: number;

  // Power range properties
  min_active_power_range_kW?: number;
  max_active_power_range_kW?: number;
  min_apparent_power_range_kW?: number;
  max_apparent_power_range_kW?: number;

  // Boolean properties
  has_switching_operation_1?: boolean;
  has_switching_operation_2?: boolean;
  has_switching_operation_3?: boolean;

  certificate?: Certificate;
}

// Utility functions for enum conversion (optional)
export class EnumUtils {
  static getNetFeedInValues(): { value: NetFeedIn; label: string }[] {
    return [
      { value: NetFeedIn.SinglePhase, label: 'Single Phase' },
      { value: NetFeedIn.TwoPhase, label: 'Two-Phase' },
      { value: NetFeedIn.ThreePhase, label: 'Three-Phase' },
      { value: NetFeedIn.Unknown, label: 'Unknown' },
    ];
  }

  static getCertificationValidationStatusValues(): { value: CertificationValidationStatus; label: string }[] {
    return [
      { value: CertificationValidationStatus.Verified, label: 'Verified' },
      { value: CertificationValidationStatus.VerificationPending, label: 'Verification Pending' },
      { value: CertificationValidationStatus.OngoingFeedbackProcess, label: 'Ongoing Feedback Process' },
    ];
  }

  static getCategoryValues(): { value: Category; label: string }[] {
    return [
      { value: Category.Inverter, label: 'Inverter' },
      { value: Category.StorageInverter, label: 'Storage Inverter' },
      { value: Category.AsynchronousGenerator, label: 'Asynchronous Generator' },
      { value: Category.SynchronousGenerator, label: 'Synchronous Generator' },
      { value: Category.FuelCell, label: 'Fuel Cell' },
      { value: Category.UndervoltageProtection, label: 'Undervoltage Protection' },
      { value: Category.PAVEMonitoring, label: 'PAV, E-Monitoring' },
      { value: Category.VoltageRegulator, label: 'Voltage Regulator' },
      { value: Category.ProtectiveDeviceIntermediate, label: 'Protective Device (Intermediate)' },
      { value: Category.EZAController, label: 'EZA Controller' },
      { value: Category.Other, label: 'Other' },
    ];
  }

  static getPrimaryEnergySourceValues(): { value: PrimaryEnergySource; label: string }[] {
    return [
      { value: PrimaryEnergySource.Wind, label: 'Wind' },
      { value: PrimaryEnergySource.Photovoltaic, label: 'Photovoltaic' },
      { value: PrimaryEnergySource.PVStorage, label: 'PV / Storage' },
      { value: PrimaryEnergySource.OtherFuels, label: 'Other Fuels' },
      { value: PrimaryEnergySource.Storage, label: 'Storage' },
      { value: PrimaryEnergySource.Water, label: 'Water' },
      { value: PrimaryEnergySource.Biogas, label: 'Biogas' },
      { value: PrimaryEnergySource.Other, label: 'Other' },
      { value: PrimaryEnergySource.NotApplicable, label: 'N/A' },
    ];
  }
}
